import {useEffect, useRef, useState} from 'react';
import {Dropdown, Spinner} from 'react-bootstrap';
import {FaCommentDots} from 'react-icons/fa';
import {useNavigate} from 'react-router-dom';
import {useChat} from './ChatProvider';
import {ensureJobWorkerChannel} from '../../service/ChatService';
import useJobChatStatus from './useJobChatStatus';
import './JobChat.css';

export default function JobPropertyChatButton({job}) {
    const chat = useChat();
    const navigate = useNavigate();
    const identity = chat.currentUser?.role === 'ADMIN' ? chat.currentUser.id : null;
    const {status, error: statusError, refresh} = useJobChatStatus(identity, job?.cos_job_id, job?.cos_job_worker);
    const [opening, setOpening] = useState(false);
    const [error, setError] = useState('');
    const [show, setShow] = useState(false);
    const request = useRef(null);
    useEffect(() => () => request.current?.abort(), [identity, job?.cos_job_id, job?.cos_job_worker]);
    if (!job?.property_id || !job?.cos_job_id) return null;
    const workers = status?.workers || [];
    const pending = (status?.property.pending_count || 0) + workers.reduce((sum, worker) => sum + worker.pending_count, 0);
    const count = value => value > 0 ? <span className="job-chat-pending">{value} pending</span> : null;

    async function open(worker) {
        if (request.current && !request.current.signal.aborted) return;
        const controller = new AbortController(); request.current = controller;
        setOpening(true); setError('');
        try {
            const result = worker
                ? await ensureJobWorkerChannel(job.cos_job_id, worker.user_id, controller.signal)
                : await chat.ensurePropertyChannel(job.property_id, job.cos_job_id, controller.signal);
            if (controller.signal.aborted) return;
            if (!result?.conversation?.id) throw new Error('Chat did not return a conversation');
            if (worker) await chat.loadConversation(result.conversation.id, controller.signal);
            if (controller.signal.aborted) return;
            setShow(false);
            navigate(`/chat/${result.conversation.id}`, worker ? {state: {jobDraft: {id: crypto.randomUUID(), userId: identity, conversationId: result.conversation.id, text: `${result.draft_address || job.full_address}\n`}}} : undefined);
        } catch (cause) {
            if (!controller.signal.aborted) { setError(cause.message || 'Unable to open chat'); refresh(); }
        } finally {
            if (request.current === controller) { request.current = null; setOpening(false); }
        }
    }
    return <span className="job-chat" onClick={event => event.stopPropagation()} onPointerDown={event => event.stopPropagation()} onDragStart={event => event.preventDefault()}>
        <Dropdown autoClose="outside" show={show} onToggle={next => { setShow(next); if (next) refresh(); }} align="end">
            <Dropdown.Toggle variant="link" className="job-chat-toggle" aria-label={`Open job chat${pending ? `, ${pending} pending` : ''}`} title={pending ? `${pending} unread messages or threads` : 'Open job chat'}>
                {opening ? <Spinner size="sm"/> : <FaCommentDots size={20} aria-hidden="true"/>}
                {pending > 0 ? <span className="job-chat-dot" aria-hidden="true"/> : null}
            </Dropdown.Toggle>
            <Dropdown.Menu role="menu" className="job-chat-menu">
                <Dropdown.Item as="button" role="menuitem" disabled={opening} onClick={() => open(null)}><span>Property Chat<small>Admin · Agency</small></span>{count(status?.property.pending_count)}</Dropdown.Item>
                {identity ? workers.length ? workers.map(worker => <Dropdown.Item as="button" role="menuitem" key={worker.user_id} disabled={opening} onClick={() => open(worker)}><span>Worker Chat<small>{worker.name}</small></span>{count(worker.pending_count)}</Dropdown.Item>) : <Dropdown.Item as="button" role="menuitem" disabled><span>Worker Chat<small>{status ? 'No active account for assigned worker' : 'Loading worker…'}</small></span></Dropdown.Item> : null}
                {error || statusError ? <div role="alert" className="job-chat-error">{error || statusError}</div> : null}
            </Dropdown.Menu>
        </Dropdown>
    </span>;
}
